import { TIMERS_IN_MS } from '@/utils/constants';
import { useEffect, useRef } from 'react';

type UseNotIdleCallback = (timeActive: number) => void;

type UseNotIdleOptions = {
    target?: HTMLElement | Document | Window | null;
    events: (keyof HTMLElementEventMap)[];
    delay?: number;
};

const DEFAULT_DELAY = TIMERS_IN_MS.ONE_MINUTE / 2;

export const useNotIdle = (callback: UseNotIdleCallback, { target, events, delay }: UseNotIdleOptions) => {
    const internalDelay = delay ?? DEFAULT_DELAY;
    const hasActivityRef = useRef<boolean>(false);
    const startTimeRef = useRef<number>(Date.now());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const callbackRef = useRef<UseNotIdleCallback>(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const handleActivity = () => {
        hasActivityRef.current = true;
    };

    const checkActivity = () => {
        if (hasActivityRef.current) {
            const timeActive = Date.now() - startTimeRef.current;
            callbackRef.current(timeActive);
            hasActivityRef.current = false;
        }
    };

    useEffect(() => {
        const eventTarget = target ?? document;

        events.forEach((event) => eventTarget.addEventListener(event, handleActivity, { passive: true }));

        intervalRef.current = setInterval(checkActivity, internalDelay);

        return () => {
            events.forEach((event) => eventTarget.removeEventListener(event, handleActivity));

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [internalDelay, events, target]);
};
